import type { UtilityContext } from '@change-corgi/core-react/utilityContext';

import { NotFoundError } from '#src/shared/error';

import type { PsfShareDataQuery, PsfShareDataQueryVariables } from './api.graphql';
import { PsfShareData } from './api.graphql';
import type { ShareFeedItem } from './shareFeed';
import { getShareFeed } from './shareFeed';
import { prefetchShareGoal } from './shareGoal';

type Petition = NonNullable<PsfShareDataQuery['petition']>;
type FeatureConfigs = PsfShareDataQuery['featureConfigs'];
export type PsfSharePageData = {
	petition: Omit<Petition, 'tagsConnection'> & { categoryTags: string[] };
	shareFeed: readonly ShareFeedItem[];
	/**
	 * false when having shared once from the share page
	 */
	initial: boolean;
	/**
	 * true when coming from the promote_or_share page
	 */
	skip: boolean;
	/**
	 * temporary estimation of signs from shares with hard-coded percentage
	 */
	signaturesFromShareCountEstimate: number;
	/**
	 * temporary estimation of signs from shares with hard-coded percentage
	 */
	signaturesFromShareCountEstimateLocalized: string;
	/**
	 * true when the variant is tiktok
	 */
	isTiktokChannelEnabled: boolean;
	/**
	 * Feature configuration flags
	 */
	featureConfigs: FeatureConfigs;
	/**
	 * Pre-fetched sharing goal for the current viewer on this petition.
	 * null when the viewer has no saved goal, is not identified, or the fetch failed.
	 */
	initialShareGoal: number | null;
};
/**
 * FIXME this hard-coded value might have been set as a temporary value for estimating sign count from shares
 * until that metric would be properly computed in our backend, which seems to never have been properly planned
 * or prioritized
 */
const SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE = 0.6;

function normalizeExperiments(experiments: PsfShareDataQuery['experiments']) {
	return {
		isTiktokChannelEnabled: experiments.supporterExperiencePsfTiktokShareButton.variation === 'tiktok',
	};
}

export async function getPsfSharePageData(
	utilityContext: UtilityContext,
	slug: string,
	{
		queryParams,
	}: {
		queryParams: { skip?: string; share?: string };
	},
): Promise<PsfSharePageData> {
	const {
		gql: { fetch },
	} = utilityContext;

	const skip = !!queryParams.skip;
	const initial = !queryParams.share;

	const [{ data }, shareFeed] = await Promise.all([
		fetch<PsfShareDataQuery, PsfShareDataQueryVariables>({
			query: PsfShareData,
			variables: {
				slugOrId: slug,
			},
			rejectOnError: true,
		}),
		getShareFeed(utilityContext, slug),
	]);

	if (!data.petition) {
		throw new NotFoundError();
	}

	const [normalized, initialShareGoal] = await Promise.all([
		normalizeData(utilityContext, {
			skip,
			initial,
			petition: data.petition,
			shareFeed,
			experiments: data.experiments,
			featureConfigs: data.featureConfigs,
		}),
		prefetchShareGoal(utilityContext, data.petition.id),
	]);

	return { ...normalized, initialShareGoal };
}

async function normalizeData(
	utilityContext: UtilityContext,
	{
		skip,
		initial,
		petition,
		shareFeed,
		experiments,
		featureConfigs,
	}: {
		skip: boolean;
		initial: boolean;
		petition: Petition;
		shareFeed: readonly ShareFeedItem[];
		experiments: PsfShareDataQuery['experiments'];
		featureConfigs: FeatureConfigs;
	},
): Promise<Omit<PsfSharePageData, 'initialShareGoal'>> {
	const { i18n } = utilityContext;
	const signaturesFromShareCountEstimate = Math.round(
		petition.signatureState.signatureCount.displayed * SIGNS_FROM_SHARE_ESTIMATION_PERCENTAGE,
	);

	const { tagsConnection, ...rest } = petition;

	const experimentFlags = normalizeExperiments(experiments);

	return {
		petition: {
			...rest,
			categoryTags: tagsConnection?.nodes.map((tag) => tag.name) ?? [],
		},
		shareFeed,
		skip,
		initial,
		signaturesFromShareCountEstimate,
		signaturesFromShareCountEstimateLocalized: i18n.localizeNumber(signaturesFromShareCountEstimate),
		...experimentFlags,
		featureConfigs,
	};
}
